<template>
  <label class="base-checkbox"
         :class="classes">
    <div class="title title-left" v-if="labelLeft || $slots.default">
      <slot>{{ label }}</slot>
    </div>

    <input type="checkbox"
           :disabled="disabled"
           v-model="isChecked"
           @change="onChange"
           @focus="onFocus"
           @blur="onBlur" />

    <div class="checkmark"></div>

    <div class="title title-right" v-if="labelRight || $slots.default">
      <slot>{{ label }}</slot>
    </div>
  </label>
</template>

<script>
  export default {
    props: {
      value: {
        required: true,
        type: Boolean
      },

      label: {
        required: false,
        type: String
      },

      labelPosition: {
        type: String,
        default: 'left' // left :: right
      },

      disabled: {
        required: false,
        type: Boolean,
        default: false
      },

      fullWidth: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        isChecked: this.value
      }
    },

    methods: {
      onChange() {
        this.$emit('input', this.isChecked)
      },

      onFocus(event) {
        this.isActive = true
        this.$emit('focus', event)
      },

      onBlur(event) {
        this.isActive = false
        this.$emit('blur', event)
      }
    },

    computed: {
      classes() {
        return [
          { 'is-checked':  this.isChecked },
          { 'is-disabled': this.disabled },
          { 'base-checkbox-full-width': this.fullWidth }
        ];
      },

      labelLeft() {
        return this.label && this.labelPosition === 'left'
      },

      labelRight() {
        return this.label && this.labelPosition === 'right'
      }
    },

    watch: {
      value: {
        handler: function(value) {
          this.isChecked = !!value
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  .base-checkbox {
    display: inline-flex;
    align-items: center;
    position: relative;
    cursor: pointer;

    &-full-width {
      width: 100%;
      justify-content: space-between;
    }

    & input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }

    .title {
      user-select: none;

      &-left {
        margin-right: 10px;
      }

      &-right {
        margin-left: 10px;
      }
    }

    $checkmark-size: 24px;

    .checkmark {
      position: relative;
      width: $checkmark-size;
      height: $checkmark-size;
      flex-shrink: 0;
      background: transparent;
      border: 2px solid #999;
      border-radius: 50%;
      transition: all .1s ease-out;
    }

    .checkmark::after {
      content: '';
      position: absolute;
      top: 45%;
      left: 60%;
      display: block;
      width: 20%;
      height: 70%;
      transform: translate(-40%, -65%) rotate(45deg);
      border: solid #3e3a3b;
      border-width: 0 ($checkmark-size/10) ($checkmark-size/10) 0;
      opacity: 0;
      transition: all .1s ease-out;
    }

    &.is-checked {
      .checkmark {
        background: darken(map_get($colors, secondary), 8%);
        border-color: transparent;

        &::after {
          opacity: 1;
        }
      }
    }

    &:not(.is-disabled):not(.is-checked):hover,
    &:not(.is-disabled):not(.is-checked).is-active {
      .checkmark {
        background: transparent;
      }

      .checkmark::after {
        border-color: #999;
        opacity: .3;
      }
    }

    &.is-disabled {
      cursor: default;

      .checkmark {
        pointer-events: none;
        background: #e6e6e6;
        opacity: .6;
      }

      .checkmark::after {
        border-color: #7b7b7b;
      }
    }
  }
</style>
